/**
 * 認可サーバーからの戻りを受ける画面（`/auth/callback`）。
 *
 * 2 つの入り方がある。
 * - 普通のリダイレクト: ここでコードを交換し、認可前に見ていたパスへ戻す
 * - 埋め込み iframe: コードを交換しない。セッション復元もトップレベルで行う
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { useAuthState, useSession } from "../auth/AuthProvider";
import { Button } from "./primitives";

export function AuthCallbackScreen({ fallbackPath }: { fallbackPath: string }) {
  const session = useSession();
  const state = useAuthState();
  const navigate = useNavigate();
  const started = useRef(false);
  const [inFrame] = useState(() => window.top !== window.self);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    const search = window.location.search;
    if (inFrame) {
      // 認証の往復はトップレベルだけで完了する。埋め込みからコードを受け付けない。
      return;
    }
    void session.completeAuthorization(search).then((returnTo) => {
      if (returnTo === null) {
        // 理由があるなら下の error の枝が出す。**理由が無いまま止まらない**
        // （code も error も付かずに戻ってきた場合。ここで待ち続けると
        // 「ログインしています」のまま永久に動かない画面になる）
        if (session.snapshot().status !== "error") {
          navigate(fallbackPath, { replace: true });
        }
        return;
      }
      // 戻り先は自分のパスだけを受け入れる（`//evil` のような外向きを弾く）
      const safe =
        returnTo.startsWith("/") && !returnTo.startsWith("//")
          ? returnTo
          : fallbackPath;
      navigate(safe, { replace: true });
    });
  }, [session, navigate, fallbackPath, inFrame]);

  if (inFrame) {
    return null;
  }

  // 見出しは状態に合わせる。拒否されたのに「ログインしています」のままだと、
  // 下の赤枠を読むまで**まだ進んでいるように見える**
  const denied = state.status === "error" && state.error !== null;

  return (
    <div className="vd-login">
      <div className="vd-login-card">
        <h1>{denied ? "ログインできませんでした" : "ログインしています"}</h1>
        {denied ? (
          <>
            <p className="vd-notice vd-notice--error" role="alert">
              {state.error}
            </p>
            <Button
              variant="primary"
              style={{ width: "100%" }}
              onClick={() => {
                void session.login({ returnTo: fallbackPath });
              }}
            >
              もう一度ログインする
            </Button>
          </>
        ) : (
          <p>認可サーバーからの応答を確かめています。</p>
        )}
      </div>
    </div>
  );
}
